import { z } from 'zod';

/**
 * Org types drive labels, defaults and report templates only. No permission
 * check may branch on the type: a university is just an org.
 */
export const ORG_TYPES = [
  'nirvik',
  'university',
  'enterprise',
  'school',
  'government',
  'nonprofit',
  'community',
] as const;
export const orgTypeSchema = z.enum(ORG_TYPES);
export type OrgType = z.infer<typeof orgTypeSchema>;

/** Types a user may create. `nirvik` orgs are platform-managed. */
export const USER_CREATABLE_ORG_TYPES = ORG_TYPES.filter((t) => t !== 'nirvik');

/**
 * What happens when someone signs in with an email on a verified domain.
 * - open:        auto-join as learner
 * - approval:    a join request lands in the org admins' queue
 * - invite_only: the domain only suggests the org; joining needs an invite
 * - managed:     the org controls the account lifecycle (enterprise, with SSO; later)
 */
export const JOIN_POLICIES = ['open', 'approval', 'invite_only', 'managed'] as const;
export const joinPolicySchema = z.enum(JOIN_POLICIES);
export type JoinPolicy = z.infer<typeof joinPolicySchema>;

/**
 * An org tree is at most three levels deep (e.g. university → affiliated
 * college → department). Depth 0 is a top-level org.
 */
export const MAX_ORG_DEPTH = 2;

/**
 * Depth of a new child given its parent's depth. Throws when the child
 * would exceed the three-level cap.
 */
export function childDepth(parentDepth: number): number {
  const depth = parentDepth + 1;
  if (depth > MAX_ORG_DEPTH) {
    throw new OrgDepthError(MAX_ORG_DEPTH + 1);
  }
  return depth;
}

export class OrgDepthError extends Error {
  constructor(maxLevels: number) {
    super(`Org trees are limited to ${maxLevels} levels`);
    this.name = 'OrgDepthError';
  }
}

/** ISO 3166-1 alpha-2 country code, upper-case. */
export const countryCodeSchema = z
  .string()
  .length(2)
  .regex(/^[A-Za-z]{2}$/)
  .transform((c) => c.toUpperCase());
